import type { TFunction } from "i18next";
import { isOrderApproved, OrderStatus, type Order } from "../types/order";
import type { StandbyOrder } from "../types/standbyOrder";
import {
  getReportClaimMembers,
  UangJalanReportStatus,
  type UangJalanReport,
} from "../types/uangJalanReport";
import type translations from "../translations/translations.json";
import { toastEmitter } from "./toastEmitter";

type TranslationKeys = keyof typeof translations.en;

export const areAllReportMembersVerified = (report: UangJalanReport): boolean => {
  const members = getReportClaimMembers(report);
  if (members.length === 0) {
    return false;
  }
  return members.every(
    (member) => member.status === OrderStatus.VERIFIED || isOrderApproved(member.status),
  );
};

export const cascadeReportVerifyIfReady = async (
  report: UangJalanReport | null,
  verifyReport: (readableId: string) => Promise<UangJalanReport>,
  refreshReport: () => Promise<UangJalanReport | null>,
): Promise<UangJalanReport | null> => {
  if (!report || report.status !== UangJalanReportStatus.IN_REVIEW) {
    return report;
  }
  if (!areAllReportMembersVerified(report)) {
    return report;
  }
  await verifyReport(report.readableId);
  return (await refreshReport()) ?? report;
};

export const emitOrderAutoVerifiedToast = (
  t: TFunction,
  refreshedReport: UangJalanReport | null | undefined,
): void => {
  const reportVerified = refreshedReport?.status === UangJalanReportStatus.VERIFIED;
  toastEmitter.emit(
    t(
      (reportVerified
        ? "reports.orderAndReportAutoVerified"
        : "reports.orderAutoVerified") as TranslationKeys,
    ),
    "success",
  );
};

export const runAutoVerifyAfterChecklistComplete = async ({
  order,
  checked,
  isChecklistComplete,
  verifyMember,
  refreshReport,
  verifyReport,
}: {
  order: Order | StandbyOrder;
  checked: boolean;
  isChecklistComplete: (member: Order | StandbyOrder) => boolean;
  verifyMember: (orderId: string) => Promise<Order | StandbyOrder>;
  refreshReport: () => Promise<UangJalanReport | null>;
  verifyReport: (readableId: string) => Promise<UangJalanReport>;
}): Promise<UangJalanReport | null> => {
  if (!checked || order.status !== OrderStatus.IN_REVIEW || !isChecklistComplete(order)) {
    return null;
  }

  await verifyMember(order._id);
  const refreshed = await refreshReport();
  return await cascadeReportVerifyIfReady(refreshed, verifyReport, refreshReport);
};
